import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TableHead,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import Scrollbar from "../scrollbar";
import { get } from "src/utils/networkUtils";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setVersusGames,
  appendVersusGames,
  clearVersusGames,
} from "src/store/slices/versusGamesSlice";
import { getDivisionByTeam } from "./utils";
import {
  formatGameMode,
  formatEntry,
  formatPrize,
  formatRecord,
  getGameModeColor,
  TABLE_HEAD,
} from "src/utils/versusGameUtils";

export default function VersusGamesTable({ isActive, seasonTypes }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [versusSettings, setVersusSettings] = useState(null);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const fetchVersusSettings = useCallback(async () => {
    try {
      const response = await get("/admin/versus/settings");
      if (response.success && response.settings) {
        setVersusSettings(response.settings);
      } else {
        console.error("Failed to fetch versus settings:", response);
      }
    } catch (error) {
      console.error("Error fetching versus settings:", error);
    }
  }, []);

  const fetchGames = useCallback(
    async (currentOffset = 0, isInitial = false) => {
      setLoading((prevLoading) => {
        if (prevLoading) return prevLoading;

        (async () => {
          try {
            const response = await get(
              `/admin/versus/games?offset=${currentOffset}&limit=${limit}`
            );
            const newGames = response.versusGames || [];

            if (isInitial) {
              setGames(newGames);
              dispatch(setVersusGames(newGames));
            } else {
              setGames((prev) => [...prev, ...newGames]);
              dispatch(appendVersusGames(newGames));
            }

            setHasMore(newGames.length === limit);
            setOffset(currentOffset + limit);
          } catch (error) {
            console.error("Error fetching games:", error);
            if (isInitial) {
              setGames([]);
              dispatch(clearVersusGames());
            }
          } finally {
            setLoading(false);
          }
        })();

        return true;
      });
    },
    [limit, dispatch]
  );

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    fetchGames(offset);
  }, [hasMore, fetchGames, offset]);

  useEffect(() => {
    if (isActive) {
      setGames([]);
      setOffset(0);
      setHasMore(true);
      fetchVersusSettings().then(() => {
        fetchGames(0, true);
      });
    }
  }, [isActive, fetchGames, fetchVersusSettings]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.querySelector(".MuiTableContainer-root");
      if (!scrollContainer || loading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        loadMore();
      }
    };

    const scrollContainer = document.querySelector(".MuiTableContainer-root");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [loading, hasMore, loadMore]);

  const RatingTypes = {
    Gold: 2,
    AllStar: 3,
  };

  const LineupPositions = {
    HockeyDefenseLeft: 26,
    HockeyDefenseRight: 27,
  };

  const getLineupDivisions = (lineup) => {
    const divisionsMap = new Set();
    lineup.forEach((card) => {
      if (card.team && card.team.trim() !== "") {
        const division = getDivisionByTeam(card.team);
        if (division) {
          divisionsMap.add(division);
        }
      }
    });
    return Array.from(divisionsMap);
  };

  const getLineupBoost = (lineup = []) => {
    let boost = 0;

    const defenders = lineup.filter(
      (card) =>
        card.playerPosition === LineupPositions.HockeyDefenseLeft ||
        card.playerPosition === LineupPositions.HockeyDefenseRight
    );
    if (
      defenders.length === 2 &&
      defenders[0].teamExternalId === defenders[1].teamExternalId
    ) {
      boost += 2;
    }

    const ratings = lineup.map((card) => card.cardRating);
    const uniqueRatings = new Set(ratings);

    if (uniqueRatings.size >= 4) {
      boost += 5;
    }

    const allGold =
      lineup.filter((card) => card.team !== null).length === lineup.length &&
      lineup.every((card) => card.cardRating === RatingTypes.Gold);

    if (allGold) {
      boost += 1;
    }

    const allAllStar = lineup.every(
      (card) => card.cardRating === RatingTypes.AllStar
    );

    if (allAllStar) {
      boost += 2;
    }

    const divisions = getLineupDivisions(lineup);
    console.log("Divisions for boost calculation:", divisions);
    if (divisions.length >= 4) {
      boost += 3;
    }

    return boost;
  };

  return (
    <>
      <Card>
        <Scrollbar>
          <TableContainer
            sx={{ minWidth: 800, maxHeight: "100vh", overflow: "auto" }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.alignRight ? "right" : "left"}
                    >
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {games.map((row) => {
                  const {
                    versusGameId,
                    gameMode,
                    opponentFpts,
                    userFpts,
                    isWin,
                    userId,
                    opponentUserId,
                    userWins,
                    userLosses,
                    opponentUsername,
                    username,
                    gameDetails,
                    versusSeasonTypeId,
                  } = row;

                  const boost = getLineupBoost(gameDetails?.userLineup || []);

                  return (
                    <TableRow hover key={versusGameId} tabIndex={-1}>
                      <TableCell align="left">
                        <Typography variant="subtitle2" noWrap>
                          <a href={`/dashboard/profile?user_id=${userId}`}>
                            {username || "N/A"}
                          </a>
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {formatRecord(userWins, userLosses)}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Grid container spacing={1}>
                          <Grid item xs={3}>
                            <Typography variant="body2" noWrap>
                              {userFpts !== null ? userFpts.toFixed(2) : "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            {" "}
                            <Typography variant="body1" noWrap>
                              <span
                                style={{
                                  fontWeight:
                                    gameDetails?.userScore >
                                    gameDetails?.opponentScore
                                      ? "bold"
                                      : "normal",
                                }}
                              >
                                {gameDetails?.userScore || 0}
                              </span>
                              {" - "}
                              <span
                                style={{
                                  fontWeight:
                                    gameDetails?.opponentScore >
                                    gameDetails?.userScore
                                      ? "bold"
                                      : "normal",
                                }}
                              >
                                {gameDetails?.opponentScore || 0}
                              </span>
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            {" "}
                            <Typography variant="body2" noWrap>
                              {opponentFpts !== null
                                ? opponentFpts.toFixed(2)
                                : "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            {" "}
                            <Typography variant="body2" noWrap>
                              {opponentUserId !== null ? (
                                <a
                                  href={`/dashboard/profile?user_id=${opponentUserId}`}
                                >
                                  {(opponentUsername || opponentUserId)
                                    .toString()
                                    .substring(0, 10)}
                                </a>
                              ) : (
                                "N/A"
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {boost}
                          {versusSettings &&
                            versusSettings.boostSettings &&
                            versusSettings.boostSettings[boost] !==
                              undefined && (
                              <span style={{ color: "gray" }}>
                                ({versusSettings.boostSettings[boost]}%)
                              </span>
                            )}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Chip
                          label={formatGameMode(gameMode, versusSeasonTypeId)}
                          color={getGameModeColor(gameMode)}
                          size="small"
                        />
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {formatEntry(gameMode)}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {isWin ? formatPrize(gameMode) : "--"}
                        </Typography>
                      </TableCell>

                      <TableCell align="left">
                        <Chip
                          label={isWin ? "Won" : "Lost"}
                          color={isWin ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>

                      <TableCell align="left">
                        <Typography variant="body2" noWrap>
                          {new Date(row.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: "America/New_York",
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Button
                          variant="text"
                          size="small"
                          sx={{
                            p: 0,
                            minWidth: "auto",
                            textTransform: "none",
                          }}
                          onClick={() =>
                            navigate(
                              `/dashboard/versus-game-details/${versusGameId}`
                            )
                          }
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && (
                  <TableRow>
                    <TableCell align="center" colSpan={10}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 3,
                        }}
                      >
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2">
                          Loading more games...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!hasMore && games.length > 0 && (
                  <TableRow>
                    <TableCell align="center" colSpan={10}>
                      <Typography
                        variant="body2"
                        sx={{ py: 2, color: "text.secondary" }}
                      >
                        No more games to load
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
    </>
  );
}
